import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { colors } from "../constants/designTokens";
import PercentageField from "./PercentageField";
import {
  DEFAULT_TAX_REGIME,
  TAX_REGIMES,
  getDefaultTaxAliquotas,
  getTotalAliquota,
} from "../services/dreService";

const labels = {
  pis: "PIS (%)",
  cofins: "COFINS (%)",
  irpj: "IRPJ (%)",
  csll: "CSLL (%)",
  iss: "ISS (%)",
};

export default function AliquotasConfigPanel({
  regime,
  aliquotas,
  onChange,
  onSave,
  saveDisabled = false,
  saving = false,
  hasChanges = false,
  helperText = "",
}) {
  const [open, setOpen] = useState(false);
  const total = getTotalAliquota(aliquotas);
  const currentRegime = TAX_REGIMES[regime] || TAX_REGIMES[DEFAULT_TAX_REGIME];
  const canEditAliquotas = Boolean(currentRegime.editable);

  function handleRegimeChange(nextRegime) {
    onChange(nextRegime, { ...TAX_REGIMES[nextRegime].aliquotas }, "regime");
  }

  function handleAliquotaChange(key, value) {
    onChange(regime, {
      ...aliquotas,
      [key]: Number(value || 0),
    }, "aliquota");
  }

  function handleReset() {
    onChange(DEFAULT_TAX_REGIME, getDefaultTaxAliquotas(), "reset");
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        mb: 1.5,
        borderRadius: 3,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: "0 10px 24px rgba(24,39,75,0.05)",
        overflow: "hidden",
      }}
    >
      <Button
        onClick={() => setOpen((current) => !current)}
        sx={{
          width: "100%",
          px: 2.4,
          py: 1.6,
          justifyContent: "space-between",
          textTransform: "none",
          color: colors.text,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box sx={{ width: 28, height: 28, borderRadius: 2, bgcolor: "#d97706", color: "#fff", display: "grid", placeItems: "center" }}>
            <SettingsOutlined sx={{ fontSize: 17 }} />
          </Box>
          <Typography sx={{ color: colors.text, fontWeight: 800 }}>
            Configuração de Alíquotas
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.4}>
          <Chip label={`Total: ${total.toFixed(2)}%`} size="small" sx={{ bgcolor: colors.surfaceSoft, color: colors.muted, fontWeight: 800 }} />
          {open ? <KeyboardArrowUpRounded sx={{ color: colors.softMuted }} /> : <KeyboardArrowDownRounded sx={{ color: colors.softMuted }} />}
        </Stack>
      </Button>

      <Collapse in={open}>
        <Box sx={{ px: 2.4, pb: 2.4, borderTop: `1px solid ${colors.border}` }}>
          <TextField
            select
            label="Regime tributario"
            fullWidth
            value={regime}
            onChange={(event) => handleRegimeChange(event.target.value)}
            sx={{ mt: 2.2 }}
          >
            {Object.entries(TAX_REGIMES).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: "column", md: "row" }} flexWrap="wrap" gap={1.6} sx={{ mt: 2 }}>
            {Object.entries(labels)
              .filter(([key]) => regime !== "ret" || key !== "iss")
              .map(([key, label]) => (
                <PercentageField
                  key={key}
                  label={label}
                  value={aliquotas[key]}
                  onChange={(value) => handleAliquotaChange(key, value)}
                  disabled={!canEditAliquotas}
                  sx={{ flex: "1 1 170px" }}
                />
              ))}
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            gap={1.4}
            sx={{ mt: 2.2 }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, color: colors.muted }}>
                Preencha os campos das alíquotas abaixo para gerar o valor que compõe a Alíquota Efetiva:{" "}
                <strong style={{ color: colors.text }}>{total.toFixed(2)}%</strong>
              </Typography>
              {helperText ? (
                <Typography sx={{ fontSize: 12, color: colors.softMuted, mt: 0.4 }}>
                  {helperText}
                </Typography>
              ) : null}
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Button
                variant="outlined"
                startIcon={<RestartAltRounded />}
                onClick={handleReset}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Restaurar padrão
              </Button>
              {onSave ? (
                <Button
                  variant="contained"
                  startIcon={<SaveRounded />}
                  onClick={onSave}
                  disabled={saveDisabled || saving || !hasChanges}
                  sx={{ textTransform: "none", fontWeight: 800 }}
                >
                  {saving ? "Salvando..." : "Salvar aliquotas"}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
